/* cspell:disable */

import TurndownService from 'turndown';

export function getClonedHTMLWithInlinedIframes() {
  // Clone the whole document to work safely
  const clonedDoc = document.documentElement.cloneNode(true) as Document;
  const clonedIframes = clonedDoc.querySelectorAll('iframe');
  const originalIframes = document.querySelectorAll('iframe');

  clonedIframes.forEach((clonedIframe, index) => {
    const originalIframe = originalIframes[index];

    try {
      const iframeDoc =
        originalIframe.contentDocument ||
        originalIframe.contentWindow?.document;
      if (!iframeDoc) {
        throw new Error('No iframe document');
      }

      const bodyContent = iframeDoc.body.cloneNode(true) as HTMLElement; // just what's visible
      const wrapper = document.createElement('div');
      wrapper.setAttribute('data-replaced-iframe', index.toString());
      wrapper.innerHTML = bodyContent?.innerHTML || '';

      clonedIframe.parentNode?.replaceChild(wrapper, clonedIframe);
    } catch (err) {
      console.warn(`⚠️ Skipping iframe ${index}: ${(err as Error)?.message}`);
    }
  });

  // Create a temporary document to serialize the full HTML
  const container = document.implementation.createHTMLDocument();
  container.replaceChild(clonedDoc, container.documentElement);

  return '<!DOCTYPE html>\n' + container.documentElement.outerHTML;
}
// List of selectors for elements to exclude from content
const EXCLUDED_SELECTORS: string[] = [
  // // UI elements
  '.chat-container',
  '.chat-input',
  '.chat-messages',
  '#pleri-chat-box',
  // // // Navigation elements
  'nav',
  'footer',
  // // // Common UI components
  // '.modal',
  // '.popup',
  // '.tooltip',
  // '.notification',
  // // Scripts and styles
  'noscript',
  'script',
  'style',
  'link',
  // Empty or hidden elements
  '[style*="display: none"]',
  '[style*="visibility: hidden"]',
  '[aria-hidden="true"]',
  // Add more selectors as needed

  // // AWS specific
  '#awsc-error-assets',
  '#awsccc-sb-a',
  '#consoleNavHeader',
  'hr',
];

// Initialize Turndown service with custom options
const turndownService = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  emDelimiter: '*',
  bulletListMarker: '-',
  strongDelimiter: '**',
});

// Add custom rules if needed
turndownService.addRule('astrikethrough', {
  filter: ['del', 's'],
  replacement: function (content: string) {
    return '~~' + content + '~~';
  },
});

turndownService.addRule('filterElements', {
  filter: (node) => {
    return EXCLUDED_SELECTORS.some((selector) => node.matches(selector));
  },
  replacement: function (_: string) {
    return '';
  },
});

// Add custom rule for tables
turndownService.addRule('table', {
  filter: 'table',
  replacement: function (_, node) {
    const table = node as HTMLTableElement;
    const rows = table.querySelectorAll('tr');
    let markdown = '\n';

    // Process header row
    const headerCells = rows[0]?.querySelectorAll('th, td');
    if (headerCells && headerCells.length > 0) {
      markdown +=
        '| ' +
        Array.from(headerCells)
          .map((cell) => cell.textContent?.trim() || '')
          .join(' | ') +
        ' |\n';
      markdown +=
        '| ' + Array(headerCells.length).fill('---').join(' | ') + ' |\n';
    }

    // Process data rows
    for (let i = 1; i < rows.length; i++) {
      const cells = rows[i].querySelectorAll('td');
      if (cells && cells.length > 0) {
        markdown +=
          '| ' +
          Array.from(cells)
            .map((cell) => cell.textContent?.trim() || '')
            .join(' | ') +
          ' |\n';
      }
    }

    return markdown;
  },
});

// Function to clean HTML content
export const cleanHtmlContent = (htmlString: string): string => {
  // Convert HTML to Markdown using Turndown
  const markdown = turndownService.turndown(htmlString);

  // Clean up excessive newlines
  return markdown
    .replace(/\n{3,}/g, '\n\n') // Replace 3+ newlines with 2
    .replace(/\n\s+\n/g, '\n\n') // Remove whitespace between newlines
    .trim(); // Trim leading/trailing whitespace
};

// The processNode function is no longer needed as Turndown handles the conversion
